<template>
  <div
    v-if="show"
    class="segment-condition-backdrop"
    @click="onBackdropClick"
  >
    <div class="segment-condition-panel">
      <header class="segment-condition-head">
        <h3>Bedingung: {{ segmentName }}</h3>
        <button
          type="button"
          class="icon"
          aria-label="Schließen"
          @click="close"
        >×</button>
      </header>

      <div class="segment-condition-body">
        <p v-if="groups.length === 0" class="empty">
          Für dieses Projekt gibt es noch nichts, worauf eine Bedingung zeigen könnte.
          Lege ein Formular oder ein Personalisierungs-Set an.
        </p>

        <div v-for="(group, gi) in draft.groups" :key="gi" class="group">
          <p v-if="gi > 0" class="joiner">oder</p>
          <div
            v-for="(row, ri) in group"
            :key="row.id"
            class="row"
            :class="{ 'row--incomplete': isIncomplete(row) }"
          >
            <span v-if="ri === 0 && gi === 0" class="fixed">Trifft zu, wenn</span>
            <span v-else-if="ri > 0" class="joiner joiner--inline">und</span>

            <!-- A row this panel may not touch: one whose operation the model
                 could not take apart, one whose value no input can give back
                 (null, a list), and one pointing at another segment, which the
                 field list deliberately does not offer. All three read out in
                 the sentence below and travel back exactly as they came in --
                 see lockNote for what each of them is told. -->
            <span v-if="row.locked" class="locked">{{ lockNote(row) }}</span>

            <template v-else>
              <select
                class="chip"
                aria-label="Feld"
                :value="selectValue(row)"
                @change="onFieldPicked(gi, ri, $event.target.value)"
              >
                <!-- A field that is gone must be visible as gone, not as an
                     empty picker that silently builds a condition that never
                     fires. -->
                <option v-if="isMissingField(row)" :value="selectValue(row)">
                  Gelöschtes Feld — bitte neu wählen
                </option>
                <!-- Keyed by position, not by label: two forms may carry the
                     SAME name, which makes two groups labelled "Antworten ·
                     Bedarfsanalyse" and a duplicate key Vue warns about. The
                     list is rebuilt whole whenever it changes, so position is
                     as stable a key as there is here. -->
                <optgroup
                  v-for="(g, fieldGroupIndex) in groups"
                  :key="fieldGroupIndex"
                  :label="g.label"
                >
                  <option
                    v-for="f in g.fields"
                    :key="`${f.kind}-${f.path}`"
                    :value="`${f.kind}|${f.path}`"
                  >{{ f.label }}</option>
                </optgroup>
              </select>

              <!-- The address bar has ONE entry in the field list and no way to
                   list the possible parameter names, so the name is typed. -->
              <input
                v-if="row.kind === 'urlParameter'"
                v-model="row.path"
                class="chip"
                type="text"
                placeholder="utm_campaign"
                aria-label="Name des Parameters"
              >

              <select v-model="row.operator" class="chip" aria-label="Vergleich">
                <option
                  v-for="o in operatorsOf(row)"
                  :key="o.jl"
                  :value="o.jl"
                >{{ o.label }}</option>
              </select>

              <select
                v-if="needsValue(row) && valuesOf(row)"
                v-model="row.value"
                class="chip"
                aria-label="Wert"
              >
                <option
                  v-for="v in valuesOf(row)"
                  :key="v.value"
                  :value="v.value"
                >{{ v.label }}</option>
              </select>
              <!-- type="text" even for a number field, and never
                   v-model.number: the leniency that leaves an untouched
                   condition alone (keepUnchangedOperation) forgives a row value
                   that is a STRING, and a number would slip past it, rewriting
                   a stored "10000" into 10000 on a mere open and save. What the
                   field's type needs is made of it once, on write
                   (coerceValue). -->
              <input
                v-else-if="needsValue(row)"
                v-model="row.value"
                class="chip"
                type="text"
                aria-label="Wert"
              >

              <span class="fixed">ist.</span>
              <span v-if="isIncomplete(row)" class="warn">{{ incompleteHint(row) }}</span>
            </template>

            <!-- OUTSIDE the v-else on purpose: a locked row cannot be edited,
                 but taking it out is the editor's own decision to make. -->
            <button
              type="button"
              class="icon row-remove"
              aria-label="Diese Zeile entfernen"
              @click="onRemoveRow(gi, ri)"
            >×</button>
          </div>
        </div>

        <!-- Without a field list a new row would have nothing to point at, and
             both buttons would simply do nothing -- worse than not being there.
             A segment can still carry a stored condition after the last form
             was deleted, so the rows above stay reachable either way. -->
        <div v-if="groups.length > 0" class="adders">
          <button type="button" class="ghost" @click="onAddRow">+ und</button>
          <button type="button" class="ghost" @click="onAddGroup">+ oder</button>
        </div>

        <p class="sentence">{{ sentence }}</p>
      </div>

      <footer class="segment-condition-foot">
        <p v-if="blocked" class="warn foot-warn">
          Eine Zeile ist noch unvollständig. Ergänze sie, oder entferne die Zeile mit dem
          Kreuz dahinter.
        </p>
        <button type="button" class="ghost" @click="close">Abbrechen</button>
        <button
          type="button"
          class="primary"
          :disabled="blocked"
          @click="applyCondition"
        >Übernehmen</button>
      </footer>
    </div>
  </div>
</template>

<script>
/**
 * A segment's condition as one readable line, replacing the chain of three
 * stacked modals (ModalPersonalizationEditor -> PageSettingsContext ->
 * ModalCreateContextItem) the project settings used to open for it.
 *
 * The Vue-2 twin of editor-v2/components/canvas/ConditionPanel.vue, and
 * deliberately built on the very same modules, so both halves of the product
 * offer the same fields, make the same per-row decisions and read a condition
 * out in the same words:
 *
 *   conditionFields.js        the field list (built by the CALLER, so a
 *                             segment can be kept out of its own list)
 *   conditionRows.js          what a row allows, and joining/removing rows
 *   segmentConditionModel.js  reading and writing a segment's `cases`
 *   conditionSentence.js      the readback
 *
 * Purely presentational: it edits a local copy and emits the finished `cases`
 * on save. It never writes into the `cases` prop it was handed.
 *
 * FOUR THINGS ARE DIFFERENT from the editor's panel, all because a SEGMENT is
 * a named condition and nothing else:
 *   - no show/hide picker: a segment has no action, and the sentence starts
 *     with "Trifft zu, wenn ";
 *   - no second rule and no "Weitere Bedingung": a segment has exactly one
 *     condition;
 *   - no refusal over mixed sources: a segment's `cases` are evaluated as one
 *     tree against one data object, so any mix is storable;
 *   - no segment in the field list: a segment must not contain itself.
 *
 * German interface texts, hardcoded, exactly like the sentence module this
 * panel shows underneath its rows (see conditionSentence.js): the wording of a
 * condition is the product here, half of it would be untranslated anyway, and
 * this product has no second interface language.
 */
import {
  addGroup, addRow, isRowIncomplete, operatorChoicesFor, operatorChoicesForRow, removeRow,
  rowAfterFieldChange, valueChoicesFor,
} from '@/components/modals/conditionRows';
import { casesFromRows, rowsFromCases } from '@/components/modals/segmentConditionModel';
import { fieldForPath } from '@/components/modals/conditionFields';
import { sentenceFor } from '@/components/modals/conditionSentence';

// A segment has no action, so its sentence cannot say "Wird gezeigt, wenn ".
// The same lead-in the segment list already reads its segments out with.
const SEGMENT_SENTENCE_LEAD = 'Trifft zu, wenn ';

/**
 * A row as this panel keeps it, out of a row as the model read it.
 *
 * Two jobs, both of them promises carried over from earlier reviews of this
 * plan:
 *
 * 1. EVERY editable value becomes TEXT. A stored number or boolean is what an
 *    input would hand back as a string anyway, and keepUnchangedOperation
 *    forgives exactly that one step -- so an untouched row still writes its
 *    stored operation back byte for byte, while the value picker (whose
 *    choices are text, e.g. '7' for a personalization set stored as 7) finds
 *    its option again. Without this the set picker stood empty and
 *    "Übernehmen" wrote a different value than the one that was stored.
 *
 * 2. A value NO INPUT CAN PRODUCE locks the row. `null` and a list are
 *    readable -- the sentence says what they are -- but a text box would give
 *    back '' and 'a,b', and the row would be rewritten on save. Locked, it
 *    goes back exactly as it came in. Same for a row pointing at another
 *    segment: the field list has none to offer, so there is nothing here to
 *    edit it with either.
 */
function draftRow(row) {
  if (row.raw !== undefined || row.kind === 'segment') return { ...row, locked: true };
  const type = typeof row.value;
  if (row.value === undefined || type === 'string') return { ...row };
  if (type === 'number' || type === 'boolean') return { ...row, value: String(row.value) };
  return { ...row, locked: true };
}

export default {
  name: 'SegmentConditionEditor',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    segmentName: {
      type: String,
      default: '',
    },
    // The segment's stored condition, in the { or: [...] } shape the reader
    // evaluates. The CALLER heals older shapes first (normalizeCases) -- two
    // places repairing stored shapes is how a round trip stops closing.
    cases: {
      type: Object,
      default: () => ({ or: [] }),
    },
    // What collectConditionFields returned. Built by the caller, and built
    // WITHOUT segments: a segment must not be able to contain itself.
    groups: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    // One rule-shaped object rather than a bare list of groups, because
    // addRow/addGroup/removeRow in conditionRows.js work on a rule. Using them
    // means a row this panel adds is built by exactly the same decisions as a
    // row whose field the editor swapped.
    draft: { groups: [] },
  }),
  computed: {
    flatFields() {
      return this.groups.reduce((all, group) => all.concat(group.fields), []);
    },
    // Saving an unfinished row writes a comparison against an empty path or a
    // missing value: a condition that is stored, looks set, and never fires.
    // The row stays, says what it is missing, and "Übernehmen" waits.
    //
    // A row whose field was DELETED does not block: it came in that way, it is
    // marked in the picker, and it leaves untouched. See isRowIncomplete.
    blocked() {
      return this.draft.groups.some((group) => group.some((row) => isRowIncomplete(row)));
    },
    sentence() {
      return sentenceFor({
        lead: String(this.$i18n && this.$i18n.locale).startsWith('en') ? 'Applies if ' : SEGMENT_SENTENCE_LEAD,
        groups: this.draft.groups,
        resolve: (row) => this.fieldForRow(row),
        language: this.$i18n && this.$i18n.locale,
      });
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) this.load();
      },
    },
  },
  methods: {
    // Rebuilds the working copy from the prop. On every OPEN, so a draft that
    // was abandoned with "Abbrechen" is gone the next time -- and never on a
    // prop change while open, which would throw away what is being typed.
    load() {
      const groups = rowsFromCases(this.cases, (path) => this.fieldTypeForPath(path))
        .map((group) => group.map(draftRow));
      this.draft = { groups };
      // A brand-new segment starts on one row instead of an empty panel with
      // only two joiner buttons in it. With no field anywhere there is nothing
      // to point it at, and the paragraph above the rows says so.
      const field = this.firstField();
      if (groups.length === 0 && field) addRow(this.draft, 0, field);
    },
    firstField() {
      return (this.groups[0] && this.groups[0].fields[0]) || null;
    },
    // The field type behind a STORED path, so the sentence writes 10000 in a
    // number field as "10.000" and the same digits in a text field as "10000".
    fieldTypeForPath(path) {
      const field = this.flatFields.find((f) => f.path === path);
      return field && field.fieldType;
    },
    // The field behind a row, or null when it is gone. The address bar has ONE
    // entry in the field list and its path is empty, while the row carries the
    // parameter name that was typed -- matching such a row on its own path
    // would find nothing and leave its comparison picker empty.
    fieldOf(row) {
      const path = row.kind === 'urlParameter' ? '' : row.path;
      return fieldForPath(this.groups, path, row.kind);
    },
    // What the sentence asks for when it needs the NAME behind a row.
    // 'urlParameter' returns null on purpose: there is no field list to look a
    // parameter up in, so the typed name IS the name, and the sentence builder
    // knows that. A segment row cannot be resolved here at all -- this panel is
    // given the field list without segments -- so it is named by the id it
    // points at rather than as "ein gelöschtes Segment", which would be a
    // guess, and usually a wrong one.
    fieldForRow(row) {
      if (row.kind === 'segment') return { label: row.segmentId };
      if (row.kind === 'urlParameter') return null;
      return this.fieldForPath(row);
    },
    fieldForPath(row) {
      return fieldForPath(this.groups, row.path, row.kind);
    },
    selectValue(row) {
      return `${row.kind}|${row.kind === 'urlParameter' ? '' : row.path}`;
    },
    isMissingField(row) {
      if (row.kind === 'urlParameter' || row.locked) return false;
      return this.fieldOf(row) === null;
    },
    isIncomplete(row) {
      return isRowIncomplete(row);
    },
    // Named per case, because "unvollständig" alone does not say what to do.
    incompleteHint(row) {
      if (row.path.trim() === '') {
        return row.kind === 'urlParameter'
          ? 'Der Name des Parameters fehlt'
          : 'Es ist kein Feld gewählt';
      }
      return 'Der Wert fehlt';
    },
    lockNote(row) {
      if (row.raw !== undefined) {
        return 'von Hand geschrieben — nicht bearbeitbar, bleibt unverändert erhalten, '
          + 'solange sie hier steht';
      }
      if (row.kind === 'segment') {
        return 'zeigt auf ein anderes Segment — nicht bearbeitbar, bleibt unverändert '
          + 'erhalten, solange sie hier steht';
      }
      return 'der gespeicherte Wert lässt sich hier nicht eingeben — nicht bearbeitbar, '
        + 'bleibt unverändert erhalten, solange sie hier steht';
    },
    // With the field deleted there is nothing left to build the list from, and
    // an empty picker next to a sentence that still reads "größer als 10.000"
    // is the panel contradicting itself. The row's own memory of its type
    // stands in, and the comparison it actually carries is always among the
    // choices.
    operatorsOf(row) {
      const field = this.fieldOf(row);
      return field ? operatorChoicesFor(field, this.$i18n && this.$i18n.locale) : operatorChoicesForRow(row, this.$i18n && this.$i18n.locale);
    },
    valuesOf(row) {
      const field = this.fieldOf(row);
      return field ? valueChoicesFor(field, this.$i18n && this.$i18n.locale) : null;
    },
    // Asked of the OPERATOR, never of the field: '!!' and '!' need nothing,
    // whatever field the row points at -- including one that is gone.
    needsValue(row) {
      return this.operatorsOf(row).some((o) => o.jl === row.operator && o.needsValue);
    },
    onFieldPicked(gi, ri, value) {
      const separator = value.indexOf('|');
      const kind = value.slice(0, separator);
      const path = value.slice(separator + 1);
      const field = this.flatFields.find((f) => f.kind === kind && f.path === path);
      if (!field) return;
      // $set, not an index assignment: Vue 2 does not see `array[i] = x`.
      this.$set(this.draft.groups[gi], ri, rowAfterFieldChange(this.draft.groups[gi][ri], field));
    },
    // The joiners and the row's own cross. Every decision behind them is in
    // conditionRows.js; these only pick the field a new row starts on -- the
    // first in the list, which the editor then changes in the row itself.
    //
    // "und" goes into the LAST alternative, the one the editor is looking at
    // the bottom of. Adding to the first would move the new line somewhere
    // else on the screen than where the button was pressed.
    onAddRow() {
      const field = this.firstField();
      if (!field) return;
      addRow(this.draft, this.draft.groups.length - 1, field);
    },
    onAddGroup() {
      const field = this.firstField();
      if (!field) return;
      addGroup(this.draft, field);
    },
    onRemoveRow(gi, ri) {
      removeRow(this.draft, gi, ri);
    },
    onBackdropClick(event) {
      if (event.target === event.currentTarget) this.close();
    },
    close() {
      this.$emit('close');
    },
    applyCondition() {
      if (this.blocked) return;
      this.$emit('apply', casesFromRows(this.draft.groups));
    },
  },
};
</script>

<style scoped>
.segment-condition-backdrop {
  position: fixed; inset: 0; background: rgba(0, 0, 0, .35);
  display: flex; align-items: center; justify-content: center; z-index: 1050;
}
.segment-condition-panel {
  background: #fff; border-radius: 8px; width: min(46rem, 92vw);
  max-height: 85vh; display: flex; flex-direction: column;
  box-shadow: 0 20px 60px -20px rgba(0, 0, 0, .5);
}
.segment-condition-head, .segment-condition-foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: .75rem; padding: 1rem 1.25rem;
}
.segment-condition-head { border-bottom: 1px solid #e9eded; }
.segment-condition-head h3 { margin: 0; font-size: 1.15rem; }
.segment-condition-foot { border-top: 1px solid #e9eded; justify-content: flex-end; }
.segment-condition-body { padding: 1.25rem; overflow-y: auto; }
.row { display: flex; flex-wrap: wrap; align-items: center; gap: .35rem; line-height: 2.2; }
.chip {
  font: inherit; color: #0e6b67; background: #e3efee;
  border: 1px solid transparent; border-bottom: 1.5px solid #0e6b67;
  border-radius: 4px 4px 0 0; padding: .1rem .45rem;
}
.fixed { color: #5b6668; }
.joiner { color: #5b6668; margin: .25rem 0; }
.joiner--inline { margin: 0 .25rem 0 0; }
.sentence { margin: .75rem 0 .25rem; padding: .6rem .8rem; background: #e3efee; border-radius: 6px; }
.adders { display: flex; gap: .5rem; margin-top: .5rem; }
.adders .ghost { padding: .2rem .7rem; }
/* Quiet until the row is under the pointer or the button has focus: one cross
   per line would otherwise shout louder than the sentence. */
.row-remove { color: #8a9698; padding: 0 .2rem; opacity: .55; }
.row:hover .row-remove, .row-remove:focus { opacity: 1; }
.locked { color: #8a6d3b; background: #f6efe2; padding: .2rem .5rem; border-radius: 4px; }
.warn { color: #a3401f; background: #fbeee9; padding: .2rem .5rem; border-radius: 4px; }
.foot-warn { margin: 0 auto 0 0; }
.row--incomplete { outline: 1px dashed #dba58f; outline-offset: 3px; border-radius: 4px; }
.primary:disabled { opacity: .45; cursor: not-allowed; }
.empty { color: #5b6668; }
.ghost { background: none; border: 1px solid #dce1e1; border-radius: 4px; padding: .4rem 1rem; cursor: pointer; }
.primary { background: #0e6b67; color: #fff; border: 0; border-radius: 4px; padding: .4rem 1rem; cursor: pointer; }
.icon { background: none; border: 0; font-size: 1.4rem; line-height: 1; cursor: pointer; }
</style>
